import { BulkVisitsActionBar } from '@/components/bulkQueue/BulkVisitsActionBar';
import { ActionsRenderCell } from '@/components/bulkQueue/cells/ActionsRender';
import { FulfillmentWindowRenderCell } from '@/components/bulkQueue/cells/FulfillmentWindowRender';
import { OrderNotesRenderCell, OrderRenderCell } from '@/components/bulkQueue/cells/OrderRender';
import { PatientRenderCell } from '@/components/bulkQueue/cells/PatientRender';
import { PriorityRenderCell } from '@/components/bulkQueue/cells/PriorityRender';
import { ClinicianRenderCell } from '@/components/bulkQueue/cells/clinician/ClinicianRender';
import {
    ClinicianSelectionCell,
    ClinicianSelectionHeader,
} from '@/components/bulkQueue/cells/clinician/ClinicianSelection';
import { DateRenderCell } from '@/components/bulkQueue/cells/date/DateRender';
import { ServicesRenderCell } from '@/components/bulkQueue/cells/services/ServicesRender';
import { BulkClinicianAssignmentTableQuery } from '@/components/bulkQueue/tables/clinician/BulkClinicianAssignmentTable.gql.types';
import {
    BulkClinicianContextProvider,
    useBulkClinicianContext,
} from '@/components/bulkQueue/tables/clinician/BulkClinicianContext';
import { getSetCellProps } from '@/components/bulkQueue/tables/components';
import { VisitTableRow } from '@/components/bulkQueue/tables/queries';
import { useConnectedBulkTable } from '@/components/bulkQueue/tables/useConnectedBulkTable';
import { Pagination } from '@/components/bulkQueue/tables/useUnifiedPagination';
import { BulkTableRow } from '@/components/bulkQueue/types';
import { rowToLink } from '@/components/bulkQueue/utils';
import { MapFriendlyVisit } from '@/components/mapping/VisitsMap/VisitsMap.types';
import { useSetMapFriendlyVisits } from '@/components/mapping/VisitsMap/useSetMapFriendlyVisits';
import { AxleTable, RawColumn, ServerSideSort } from '@/components/tables/AxleTable';
import { useReassignVisitPermissionsFilter } from '@/components/visits/dialogs/ReassignVisits/reassignVisitPermissions';
import { EntrySource } from '@/components/visits/dialogs/ReassignVisits/v3/tracking/entrySource';
import { VisitColumns } from '@/components/visits/tables/columns';
import { visitOrVisitRequestTableRowFragmentToMapFriendlyVisits } from '@/components/visits/utils/visitOrVisitRequestTableRowFragmentToMapFriendlyVisits';
import { PAGE_BODY_SKELETON_HEIGHT, SimpleSkeleton } from '@/fetching/suspense';
import { useHasVisitCancelPermission } from '@/hooks/permissions/visit';
import { FeatureFlag } from '@/types/featureFlag';
import { VisitState } from '@/types/visit';
import { useFeatureFlagEnabled } from '@/utils/useFeatureFlagEnabled';
import { gql } from '@apollo/client';
import { Checkbox, CheckboxProps, Typography } from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';

export const BULK_CLINICIAN_ASSIGNMENT_TABLE_QUERY = gql`
    query BulkClinicianAssignmentTable(
        $visitFilters: VisitFilter
        $visitPagination: PaginationInput
        $visitOrdering: VisitOrdering
    ) {
        visits(filters: $visitFilters, pagination: $visitPagination, ordering: $visitOrdering) {
            totalCount
            items {
                ...VisitTableRow
            }
        }
    }
    ${VisitTableRow}
`;

type BulkClinicianAssignmentTableProps = {
    title?: string;
    rows: BulkTableRow[];
    pagination: Pagination;
    serverSideSort: ServerSideSort;
    hasBulkReassign: boolean;
    selectedRowValues: string[];
    setSelectedRowValues: (values: string[]) => void;
};

const getColumns = (
    isLocked: boolean,
    hasEditableItems: boolean,
    rows: BulkTableRow[],
    hasOrders: boolean,
): RawColumn<BulkTableRow>[] => [
    {
        field: 'sortPriority',
        label: 'Priority',
        rowToComponent: (entity) => <PriorityRenderCell entity={entity} />,
        options: { ...VisitColumns.priorityColumnOptions, sort: false },
    },
    {
        field: 'patient.fullName',
        label: 'Patient',
        rowToComponent: (entity) => <PatientRenderCell entity={entity} />,
    },
    {
        field: 'sortServicesName',
        label: 'Services',
        rowToComponent: (entity) => <ServicesRenderCell entity={entity} />,
        options: { sort: false },
    },
    {
        field: 'cliniciansSortKey',
        label: 'Clinician',
        rowToComponent: (entity) =>
            entity.__typename === 'Visit' && !entity.permissions.assign ? (
                <ClinicianRenderCell entity={entity} />
            ) : (
                <ClinicianSelectionCell entity={entity} isLocked={isLocked} />
            ),
        options: {
            sort: false,
            setCellProps: (_value: unknown, rowIndex: number) => {
                const row = rows[rowIndex];
                const isEditable = row.__typename !== 'Visit' || row.permissions.assign;
                return getSetCellProps(hasEditableItems && isEditable ? true : null);
            },
            customHeadRender: (columnMeta, handleToggleColumn, sortOrder) => (
                <ClinicianSelectionHeader
                    key={columnMeta.name}
                    columnMeta={columnMeta}
                    handleToggleColumn={handleToggleColumn}
                    sortOrder={sortOrder}
                    {...{ isLocked, hasEditableItems }}
                />
            ),
        },
    },
    {
        field: 'sortDate',
        label: 'Date',
        rowToComponent: (entity) => <DateRenderCell entity={entity} />,
    },
    {
        field: 'fulfillmentWindow.startDate',
        label: 'Fulfillment Window',
        rowToComponent: (entity) => <FulfillmentWindowRenderCell entity={entity} />,
    },
    ...(hasOrders
        ? [
              {
                  field: 'orderSortKey',
                  label: 'Order',
                  rowToComponent: (entity: BulkTableRow) => <OrderRenderCell entity={entity} />,
                  options: { sort: false },
              },
              {
                  label: 'Order Notes',
                  rowToComponent: (entity: BulkTableRow) => (
                      <OrderNotesRenderCell entity={entity} />
                  ),
              },
          ]
        : []),
    {
        field: '',
        label: 'Actions',
        rowToComponent: (entity) => <ActionsRenderCell entity={entity} />,
        options: { sort: false },
    },
];

const rowToSelectionId = (row: BulkTableRow) => row.id;

export const BulkClinicianAssignmentTable: FC<BulkClinicianAssignmentTableProps> = (props) => {
    const {
        rows,
        pagination,
        title,
        serverSideSort,
        hasBulkReassign,
        selectedRowValues,
        setSelectedRowValues,
    } = props;

    const { selectedCount: clinicianSelectedCount } = useBulkClinicianContext();
    const hasClinicianSelections = clinicianSelectedCount > 0;
    const isLocked = selectedRowValues.length > 0;
    const hasEditableItems = rows.some((r) => r.__typename !== 'Visit' || r.permissions.assign);
    // Hide row selection (and its select-all header checkbox) when there are no
    // rows to select. AxleTable requires all selection props to agree.
    const showBulkSelect = hasBulkReassign && rows.length > 0;
    const hasOrders = useFeatureFlagEnabled(FeatureFlag.ORDERS);
    const columns = useMemo(
        () => getColumns(isLocked, hasEditableItems, rows, hasOrders),
        [isLocked, hasEditableItems, rows, hasOrders],
    );

    const disabledCheckboxComponents = useMemo(
        () => ({
            Checkbox: (props: CheckboxProps) => <Checkbox {...props} disabled />,
        }),
        [],
    );

    return (
        <AxleTable
            title={
                typeof title === 'string' ? (
                    <Typography variant='h2'>{title}</Typography>
                ) : undefined
            }
            dynamicPaging={pagination}
            data={rows}
            rowToLink={rowToLink}
            columns={columns}
            toolbar={false}
            serverSideSort={serverSideSort}
            selectableRows={showBulkSelect ? 'multiple' : 'none'}
            rowToSelectionIdentifier={showBulkSelect ? rowToSelectionId : undefined}
            selectedRowValues={showBulkSelect ? selectedRowValues : undefined}
            setSelectedRowValues={showBulkSelect ? setSelectedRowValues : undefined}
            components={hasClinicianSelections ? disabledCheckboxComponents : undefined}
        />
    );
};

type BulkActionsBarProps = {
    rows: BulkTableRow[];
    selectedRowValues: string[];
    hasBulkReassign: boolean;
    hasBulkCancel: boolean;
};

const BulkActionsBar: FC<BulkActionsBarProps> = ({
    rows,
    selectedRowValues,
    hasBulkReassign,
    hasBulkCancel,
}) => {
    const selectedSet = useMemo(() => new Set(selectedRowValues), [selectedRowValues]);
    const reassignVisitFilter = useReassignVisitPermissionsFilter();

    const reassign = useMemo(() => {
        const visitIds: string[] = [];
        rows.forEach((row) => {
            if (!selectedSet.has(row.id) || row.__typename === 'VisitRequest') {
                return;
            }
            if (!reassignVisitFilter || reassignVisitFilter(row.permissions)) {
                visitIds.push(row.id);
            }
        });
        return {
            visitIds,
            entrySource: EntrySource.BulkClinicianAssignmentTable,
        };
    }, [rows, selectedSet, reassignVisitFilter]);

    const cancellableVisits = useMemo(
        () =>
            rows
                .filter(
                    (row) =>
                        row.__typename === 'Visit' &&
                        selectedSet.has(row.id) &&
                        row.state !== VisitState.ENDED &&
                        row.permissions.cancel,
                )
                .map((row) => ({
                    visitId: row.id,
                    patientId: row.patient.id,
                    patientName: row.patient.fullName,
                    city: row.address?.city,
                })),
        [rows, selectedSet],
    );

    return (
        <BulkVisitsActionBar
            selectedCount={selectedRowValues.length}
            cancellableVisits={cancellableVisits}
            showCancel={hasBulkCancel}
            showReassign={hasBulkReassign}
            reassign={reassign}
        />
    );
};

type ConnectedBulkClinicianAssignmentTableProps = {
    title?: string;
};

export const ConnectedBulkClinicianAssignmentTable: FC<
    ConnectedBulkClinicianAssignmentTableProps
> = (props) => {
    const { title } = props;
    const { rows, pagination, serverSideSort, showSkeleton } =
        useConnectedBulkTable<BulkClinicianAssignmentTableQuery>({
            query: BULK_CLINICIAN_ASSIGNMENT_TABLE_QUERY,
        });
    const hasBulkReassign = useFeatureFlagEnabled(FeatureFlag.DASHBOARD_BULK_VISIT_REASSIGNMENT);
    const bulkCancelEnabled = useFeatureFlagEnabled(
        FeatureFlag.DASHBOARD_BULK_CANCELLATION_ALL_VISITS,
    );
    const hasBulkCancel = useHasVisitCancelPermission() && bulkCancelEnabled;
    const hasBulkActions = hasBulkReassign || hasBulkCancel;
    const [selectedRowValues, setSelectedRowValues] = useState<string[]>([]);

    // Prune selections to only include IDs still in the displayed rows.
    // When filters change and rows update, stale selections are removed.
    useEffect(() => {
        const rowIds = new Set(rows.map((r) => r.id));
        setSelectedRowValues((prev) => {
            const filtered = prev.filter((id) => rowIds.has(id));
            return filtered.length === prev.length ? prev : filtered;
        });
    }, [rows]);

    const setMapFriendlyVisits: () => MapFriendlyVisit[] = useCallback(() => {
        return visitOrVisitRequestTableRowFragmentToMapFriendlyVisits(rows);
    }, [rows]);

    useSetMapFriendlyVisits(setMapFriendlyVisits);

    if (showSkeleton) {
        return <SimpleSkeleton height={PAGE_BODY_SKELETON_HEIGHT} />;
    }

    return (
        <BulkClinicianContextProvider entities={rows}>
            {hasBulkActions && (
                <BulkActionsBar
                    rows={rows}
                    selectedRowValues={selectedRowValues}
                    hasBulkReassign={hasBulkReassign}
                    hasBulkCancel={hasBulkCancel}
                />
            )}
            <BulkClinicianAssignmentTable
                rows={rows}
                title={title}
                pagination={pagination}
                serverSideSort={serverSideSort}
                hasBulkReassign={hasBulkActions}
                selectedRowValues={selectedRowValues}
                setSelectedRowValues={setSelectedRowValues}
            />
        </BulkClinicianContextProvider>
    );
};
